import {
  PERSPECTIVE_TAB_TOOLTIPS,
  type PerspectiveCategory,
} from '@/app/common/blocks/perspectiveTabContext'
import {
  EPILOGUE_TOOLTIP,
  EPILOGUE_ENTRY,
  type TimelineEndpointEntry,
} from '../planATimelineDiagramModel'
import { type TimelinePlacement } from '../timelineDiagramLayout'
import { type SelectedEnding } from '../useSelectedEnding'
import { DiagramLabel } from './DiagramLabel'

// The right-hand stack: the Epilogue riding the spine's right end, then the
// alternate-perspective labels on their own tracks just below it, in line with
// Epilogue. Positions come from PLACEMENT.
//
// Whichever ending is currently open carries the bold/large `emphasis` — the
// same highlight Plan A wears when nothing is open (PlanLabels drops it then), so
// the diagram always emphasizes exactly the section being read.
//
// "Open" has two sources, folded together here: `selectedEnding` (the live hash,
// used everywhere an ending expands IN PLACE — the main page + inline Plan A) and
// `selected` (the page-level `?perspective=` mode, the only signal on a POV's own
// standalone flat page, where there's no in-place expansion or hash).
export const EndpointLabels = ({
  placement,
  selected,
  selectedEnding,
  onSelect,
}: {
  placement: TimelinePlacement
  selected: PerspectiveCategory
  selectedEnding: SelectedEnding | null
  onSelect: (entry: TimelineEndpointEntry) => void
}) => {
  // Local binding so the truthiness guard narrows away `undefined` inside the
  // onClick closure (an imported const won't narrow into a nested function).
  const epilogue = EPILOGUE_ENTRY

  return (
    <>
      {epilogue && (
        <DiagramLabel
          x={placement.epilogueLabelAt.x}
          y={placement.epilogueLabelAt.y}
          anchor="start"
          overlay
          emphasis={selectedEnding === 'epilogue'}
          tooltip={EPILOGUE_TOOLTIP}
          onClick={() => onSelect(epilogue)}
        >
          {epilogue.label}
        </DiagramLabel>
      )}

      {placement.perspectives.map(({ entry, labelAt }) =>
        entry.kind === 'perspective' ? (
          <DiagramLabel
            key={entry.label}
            x={labelAt.x}
            y={labelAt.y}
            anchor="start"
            overlay
            emphasis={selectedEnding === entry.perspective || selected === entry.perspective}
            tooltip={PERSPECTIVE_TAB_TOOLTIPS[entry.perspective]}
            onClick={() => onSelect(entry)}
          >
            {entry.label}
          </DiagramLabel>
        ) : null,
      )}
    </>
  )
}
